import * as React from 'react';

import { IconPosition, Size, Variant, Color } from './types';
import * as S from './styles';

const renderChildrenWithIcon = (
  iconPosition: IconPosition,
  icon: React.ReactNode,
  children: React.ReactNode,
  size: Size
) => {
  switch (iconPosition) {
    case 'bottom':
    case 'right':
      return (
        <>
          {children}
          {icon && (
            <S.IconWrapper iconPosition={iconPosition} size={size}>
              {icon}
            </S.IconWrapper>
          )}
        </>
      );
    default:
      return (
        <>
          {icon && (
            <S.IconWrapper iconPosition={iconPosition} size={size}>
              {icon}
            </S.IconWrapper>
          )}
          {children}
        </>
      );
  }
};

type Props = {
  children: React.ReactNode;
  className: string;
  style: React.CSSProperties;
  disabled: boolean;
  type: string;
  tabIndex: number | string;
  icon: React.ReactNode;
  iconOutlined: React.ReactNode;
  href: string;
  onClick: (e: React.MouseEvent) => void;
  iconPosition: IconPosition;
  size: Size;
  variant: Variant;
  color: Color;
  ariaLabel: string;
  component: React.ReactNode;
  truncate: boolean;
  'data-test': string;
  'data-testid': string;
  title: string;
  active: boolean;
  scaleIcon: boolean;
};

const Button = (props: Props) => {
  const {
    component,
    children,
    className,
    style,
    disabled,
    iconPosition,
    icon,
    iconOutlined,
    onClick,
    tabIndex,
    href,
    variant,
    size,
    color,
    ariaLabel,
    type,
    truncate,
    title,
    active,
    scaleIcon,
  } = props;

  return (
    <S.Button
      component={
        // eslint-disable-next-line jsx-a11y/anchor-has-content, jsx-a11y/control-has-associated-label
        href ? <a href={href} /> : component
      }
      type={type}
      aria-label={ariaLabel}
      isLink={!!href || color === 'link'}
      onClick={onClick}
      iconPosition={iconPosition}
      variant={variant}
      size={size}
      className={className}
      style={style}
      tabIndex={disabled ? '-1' : tabIndex}
      disabled={disabled}
      color={color}
      data-test={props['data-test']}
      data-testid={props['data-testid']}
      title={title}
      active={active}
      scaleIcon={scaleIcon}
    >
      <S.Inner
        variant={variant}
        iconPosition={iconPosition}
        truncate={truncate}
      >
        {['icon', 'iconOutlined'].includes(variant)
          ? icon && (
              <S.IconWrapper
                noMargin={React.Children.count(children) <= 1}
                iconPosition={iconPosition}
                size={size}
              >
                {variant === 'iconOutlined' && iconOutlined && !active
                  ? iconOutlined
                  : icon}
              </S.IconWrapper>
            )
          : renderChildrenWithIcon(iconPosition, icon, children, size)}
      </S.Inner>
    </S.Button>
  );
};

Button.defaultProps = {
  children: null,
  className: undefined,
  type: 'button',
  style: undefined,
  disabled: false,
  tabIndex: '0',
  onClick: undefined,
  icon: undefined,
  iconOutlined: undefined,
  href: undefined,
  iconPosition: 'left',
  size: 'medium',
  variant: 'default',
  color: 'secondary',
  ariaLabel: undefined,
  component: <button type="button" />, // eslint-disable-line jsx-a11y/control-has-associated-label
  truncate: false,
  'data-test': undefined,
  'data-testid': undefined,
  title: '',
  active: undefined,
  scaleIcon: true,
};

export default Button;
